<template>
  <Button
    v-if="isUserbackEnabled"
    variant="text"
    size="xs"
    :class="feedbackButtonClasses"
    :aria-label="t('routes.giveFeedback')"
    :tooltip="showTooltip ? t('routes.giveFeedback') : undefined"
    tooltip-position="right"
    @click="userbackStore.open()"
  >
    <span class="flex items-center gap-2">
      <ChatBubbleLeftIcon v-if="showIcon" class="w-6" />
      <span v-if="showText">{{ t('routes.giveFeedback') }}</span>
    </span>
  </Button>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';
import { storeToRefs } from 'pinia';
import i18n from '@/plugins/vue-i18n';
import { Button } from '@boqnet/ui';
import { ChatBubbleLeftIcon } from '@heroicons/vue/24/outline';
import { type UserbackOptions } from '@userback/widget';
import { useUserbackStore } from '@/stores/userback.store';
import { useAuthStore } from '@/stores/auth.store';
import { acceptedConsentVersion, refreshAcceptedConsentVersion } from '@/plugins/post-hog';
import { isStaging } from '@/utils/flags';

const MIN_USERBACK_CONSENT_VERSION = 2;
const userbackToken = import.meta.env.VITE_APP_USERBACK;

interface Props {
  /** `sidebar` = desktop nav (supports the collapsed icon state); `mobile` = mobile nav drawer. */
  variant?: 'sidebar' | 'mobile';
  /** Sidebar-only: hide the text label, keeping just the icon. No effect for `mobile`. */
  collapsed?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'sidebar',
  collapsed: false,
});

const { t } = i18n.global;

const userbackStore = useUserbackStore();

const authStore = useAuthStore();
const { loggedInUser } = storeToRefs(authStore);

// Sync the consent mirror to the current user; `immediate` covers page refresh.
watch(() => loggedInUser.value?.id, refreshAcceptedConsentVersion, { immediate: true });

// Context: consent is the only gate on the feedback entry point — a user who
// hasn't accepted a covering version simply gets no feedback control (PLE-1114).
// - Production: require THIS user to have accepted a covering version. Compared
//   against MIN_USERBACK_CONSENT_VERSION rather than the live CONSENT_VERSION so
//   a later bump doesn't hide Userback from users who already accepted the
//   version that added it.
// - Staging: unconditional. Users aren't added to PostHog there, so no per-user
//   consent record exists to compare against.
const isUserbackEnabled = computed(
  () => isStaging || acceptedConsentVersion.value >= MIN_USERBACK_CONSENT_VERSION,
);

// Load the widget as soon as the consent gate opens, so it's fully mounted and
// opens on the first click.
watch(
  isUserbackEnabled,
  (enabled) => {
    if (!enabled || !userbackToken) return;

    const user = loggedInUser.value;
    const options: UserbackOptions = {
      // Suppress Userback's own floating launcher — our button is the only trigger.
      widget_settings: { trigger_type: 'api' },
      ...(user && {
        user_data: { id: user.id, info: { name: user.firstName, email: user.email } },
      }),
    };
    void userbackStore.init(userbackToken, options);
  },
  { immediate: true },
);

const isSidebar = computed(() => props.variant === 'sidebar');

const showIcon = computed(() => isSidebar.value);
const showText = computed(() => !isSidebar.value || !props.collapsed);

// Matches the nav items above: the label only needs a tooltip once the rail has
// collapsed it away.
const showTooltip = computed(() => isSidebar.value && props.collapsed);

// A translucent white lift on hover, as the org switcher above uses. `!no-underline`
// cancels the Button `text` variant's link underline, which reads as a text link.
const navHoverClasses = 'rounded-lg !no-underline hover:bg-white/10';

const collapsedIconClasses = `mb-3 flex w-full justify-center !p-2 !text-white ${navHoverClasses}`;

// Spacing above the settings button is a margin, not padding, so the hover fill
// hugs the row instead of bleeding into the gap.
const expandedClasses = `mb-4 flex w-full !justify-start !px-2 !py-2 !text-white ${navHoverClasses}`;

const feedbackButtonClasses = computed(() => {
  if (!isSidebar.value) return '!text-white pb-4 pl-2';
  return props.collapsed ? `${collapsedIconClasses} 3xl:mb-6` : `${expandedClasses} 3xl:mb-6`;
});
</script>
